import { CustomMDX } from "@acme/mdx";
import { imageCdnFullWidthProps } from "@acme/ui/image-cdn";
import { Link } from "@tanstack/react-router";
import { Image } from "@unpic/react";
import type { ReactNode } from "react";
import { ArticleToc } from "~/components/blog/article-toc";
import { NewTab } from "~/components/inside/new-tab";
import { ProjectStatusTag } from "~/components/projects/project-status-tag";
import { mdxLocaleProps } from "~/lib/mdx-locale";
import type { PublicProject, PublicProjectSummary } from "~/lib/server/project";
import * as m from "~/paraglide/messages.js";
import { getLocale, localizeHref } from "~/paraglide/runtime.js";

/**
 * Which locale the requested slug belongs to.
 *
 * The current locale wins when its slug matches. Scanning first looked
 * equivalent but is not: entries are checked in object key order, so a piece
 * whose locales share one slug — most of the portfolio, where "camping" is
 * "camping" in both — resolved to whichever key happened to come first. That
 * made the English page announce a Latvian URL in its structured data while
 * its own canonical said otherwise.
 *
 * The scan stays as the fallback for the genuinely useful case: arriving on a
 * slug that belongs to the other locale.
 */
export function findSlugLocale(
  localeSlugs: Record<string, string>,
  slug: string | undefined
) {
  const currentLocale = getLocale();
  if (slug !== undefined && localeSlugs[currentLocale] === slug) {
    return currentLocale;
  }

  return (
    Object.entries(localeSlugs).find(
      ([, localeSlug]) => localeSlug === slug
    )?.[0] ?? currentLocale
  );
}

/**
 * Keeps an em dash on the line of the word it follows.
 *
 * A title set at display size breaks often, and a line opening with "—" reads
 * as a mistake rather than a pause.
 */
function bindDash(title: string) {
  return title.replace(/ — /g, "\u00a0— ");
}

/**
 * The project page body. The public route and the reviewer preview both render
 * this, so what a reviewer approves is exactly what a reader will see.
 */
export function ProjectView({
  banner,
  project,
  related = [],
}: Readonly<{
  banner?: ReactNode;
  project: PublicProject;
  related?: PublicProjectSummary[];
}>) {
  const {
    title,
    description,
    demoUrl,
    imageUrl,
    contentHtml,
    coverCaption,
    industry,
    workType,
    status,
    toc,
  } = project;

  return (
    <div className="wrap">
      {banner}
      <header className="case-header">
        <Link className="back-link" to="/projects">
          <span aria-hidden="true">←</span> {m.project_back()}
        </Link>

        {/* Titles here run two ways: a bare brand name ("Pūķarags") or a
            brand plus what the work is ("Meldri — Direct Booking Concept for
            Campsites & Glamping"). The poster size only holds for the short
            ones, so the long ones take a wider column at a smaller size. */}
        <div className="case-title-row" data-long={title.length > 24}>
          <div>
            <ProjectStatusTag status={status} />
            <h1>{bindDash(title)}</h1>
          </div>
          {description ? <p>{description}</p> : null}
        </div>

        {/* The facts a reader wants before deciding to read the write-up. Each
            one is omitted rather than shown empty. */}
        <dl className="case-facts">
          {industry ? (
            <div>
              <dt>{m.project_fact_industry()}</dt>
              <dd>{industry}</dd>
            </div>
          ) : null}
          {workType ? (
            <div>
              <dt>{m.project_fact_work()}</dt>
              <dd>{workType}</dd>
            </div>
          ) : null}
          <div>
            <dt>{m.project_fact_status()}</dt>
            <dd>
              {status === "concept"
                ? m.project_status_concept()
                : m.project_status_delivered()}
            </dd>
          </div>
          {demoUrl ? (
            <div>
              <dt>{m.project_fact_explore()}</dt>
              <dd>
                <a href={demoUrl} rel="noopener noreferrer" target="_blank">
                  {m.project_visit_site()}
                  <NewTab />
                </a>
              </dd>
            </div>
          ) : null}
        </dl>
      </header>

      {imageUrl ? (
        <figure className="project-cover">
          <Image
            alt=""
            fetchPriority="high"
            layout="fullWidth"
            loading="eager"
            sizes="(max-width: 720px) 100vw, 1130px"
            src={imageUrl}
            {...imageCdnFullWidthProps(imageUrl)}
          />
          {coverCaption ? <figcaption>{coverCaption}</figcaption> : null}
        </figure>
      ) : null}

      <div className="case-layout">
        <ArticleToc
          end={{ label: m.project_toc_end(), to: "/services" }}
          title={m.project_toc_title()}
          toc={toc ?? []}
        />

        <article className="article-body mdx-content">
          <CustomMDX
            html={contentHtml ?? ""}
            {...mdxLocaleProps(
              {
                contentLocale: findSlugLocale(
                  project.localeSlugs,
                  project.slug
                ),
              },
              (href) => localizeHref(href)
            )}
          />
        </article>
      </div>

      {related.length > 0 ? (
        <section className="related-section">
          <div className="related-heading">
            <h2>{m.project_related_title()}</h2>
            <Link to="/projects">{m.project_related_all()}</Link>
          </div>
          <ul className="related-links">
            {related.map((other) => (
              <li key={other.slug}>
                <Link
                  params={{ projectId: other.slug }}
                  to="/projects/$projectId"
                >
                  <ProjectStatusTag status={other.status} />
                  <h3>{other.title}</h3>
                  <span className="read-link">
                    {m.projects_view()} <span aria-hidden="true">↗</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
